import { Fragment, useMemo, useState, type ReactNode } from 'react';
import {
  defaultOf,
  enumOf,
  fieldsOf,
  patternOf,
  typeLabel,
  type Field,
  type OpenApiSpec,
  type Schema,
} from './openapi';

// ---------------------------------------------------------------------------
// Markdown
// ---------------------------------------------------------------------------
// The prose comes from the spec's own description, so it is authored in Markdown
// for the sake of the ReDoc artifact. Rather than duplicate it in JSX — two
// copies of the same paragraphs, one of which would eventually be wrong — this
// renders the small subset that description actually uses: paragraphs, ordered
// and unordered lists, bold, and inline code. Not a general Markdown engine, and
// not fed anything but our own text.

/** `**bold**` and `` `code` ``. */
function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    if (match[1] !== undefined) {
      // Recurse: bold spans in the description contain inline code.
      parts.push(
        <strong key={match.index} className="font-semibold text-surface-800">
          {inline(match[1])}
        </strong>,
      );
    } else {
      parts.push(
        <code key={match.index} className="font-mono text-[0.8125rem] text-primary-600">
          {match[2]}
        </code>,
      );
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  return parts;
}

export function Markdown({ source }: { source: string }) {
  const blocks = useMemo(() => {
    const lines = (source ?? '').split('\n');
    const out: ReactNode[] = [];
    let paragraph: string[] = [];
    let list: { ordered: boolean; items: string[] } | null = null;

    const flushParagraph = () => {
      if (!paragraph.length) return;
      out.push(
        <p key={out.length} className="text-sm leading-relaxed text-surface-600">
          {inline(paragraph.join(' '))}
        </p>,
      );
      paragraph = [];
    };
    const flushList = () => {
      if (!list) return;
      const { ordered, items } = list;
      const Tag = ordered ? 'ol' : 'ul';
      out.push(
        <Tag
          key={out.length}
          className={`ml-5 space-y-1 text-sm leading-relaxed text-surface-600 ${
            ordered ? 'list-decimal' : 'list-disc'
          }`}
        >
          {items.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </Tag>,
      );
      list = null;
    };

    for (const line of lines) {
      const heading = /^#{4,}\s+(.*)$/.exec(line);
      const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
      const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);

      if (heading) {
        flushParagraph();
        flushList();
        out.push(
          <h4 key={out.length} className="text-sm font-semibold text-surface-800">
            {inline(heading[1])}
          </h4>,
        );
      } else if (bullet || numbered) {
        flushParagraph();
        const ordered = Boolean(numbered);
        const item = (bullet ?? numbered)![1];
        if (list && list.ordered !== ordered) flushList();
        list = list ?? { ordered, items: [] };
        list.items.push(item);
      } else if (!line.trim()) {
        flushParagraph();
        flushList();
      } else if (list) {
        // An indented continuation of the previous list item.
        list.items[list.items.length - 1] += ` ${line.trim()}`;
      } else {
        paragraph.push(line.trim());
      }
    }
    flushParagraph();
    flushList();

    return out;
  }, [source]);

  return <div className="space-y-3">{blocks}</div>;
}

// ---------------------------------------------------------------------------
// Code
// ---------------------------------------------------------------------------

const JSON_TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

/** Colour the payload, so a 40-line body can be skimmed rather than read. */
function highlightJson(json: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  JSON_TOKEN.lastIndex = 0;
  while ((match = JSON_TOKEN.exec(json))) {
    if (match.index > cursor) parts.push(json.slice(cursor, match.index));
    const [whole, str, colon, literal, num] = match;

    if (str !== undefined && colon) {
      parts.push(
        <Fragment key={match.index}>
          <span className="text-sky-300">{str}</span>
          <span className="text-surface-400">{colon}</span>
        </Fragment>,
      );
    } else if (str !== undefined) {
      parts.push(<span key={match.index} className="text-emerald-300">{str}</span>);
    } else if (literal !== undefined) {
      parts.push(<span key={match.index} className="text-purple-300">{literal}</span>);
    } else {
      parts.push(<span key={match.index} className="text-amber-300">{num}</span>);
    }
    cursor = match.index + whole.length;
  }
  if (cursor < json.length) parts.push(json.slice(cursor));

  return parts;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard is unavailable over plain HTTP; the text is selectable anyway.
    }
  };

  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-surface-200 text-[11px] text-surface-500 hover:text-primary-600 hover:border-primary-200 transition-colors"
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/**
 * A labelled, copyable snippet. Nothing is folded away: a reference is read by
 * scrolling it, and a payload behind a toggle is a payload nobody sees.
 */
export function CodeBlock({
  label,
  lang,
  code,
  children,
}: {
  label: string;
  lang?: string;
  code: string;
  children?: ReactNode;
}) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[11px] uppercase tracking-wide text-surface-400">
          {label}
          {lang && <span className="ml-1 text-surface-300">· {lang}</span>}
        </span>
        <CopyButton value={code} />
      </div>
      <pre className="overflow-x-auto max-h-[32rem] rounded-lg bg-surface-900 px-3 py-2.5 font-mono text-xs leading-relaxed text-surface-200">
        <code>{children ?? code}</code>
      </pre>
    </div>
  );
}

/** A JSON payload, coloured. */
export function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const json = useMemo(() => JSON.stringify(value, null, 2) ?? 'null', [value]);

  return (
    <CodeBlock label={label} lang="json" code={json}>
      {highlightJson(json)}
    </CodeBlock>
  );
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** One field's row, plus its children's when it nests and is opened. */
function FieldRows({ spec, field, depth }: { spec: OpenApiSpec; field: Field; depth: number }) {
  const [open, setOpen] = useState(false);
  const children = useMemo(() => fieldsOf(spec, field.schema), [spec, field.schema]);
  const expandable = children.length > 0;
  const options = enumOf(spec, field.schema);
  const defaultValue = defaultOf(spec, field.schema);
  const pattern = patternOf(spec, field.schema);

  return (
    <>
      <tr
        className={expandable ? 'cursor-pointer hover:bg-surface-50' : undefined}
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
      >
        <td className="px-3 py-2 align-top whitespace-nowrap" style={{ paddingLeft: `${0.75 + depth * 1.1}rem` }}>
          <span className="inline-flex items-baseline gap-1">
            <span className="w-3 shrink-0 text-surface-400">
              {expandable && (
                <svg
                  className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </span>
            <code className="font-mono text-primary-600">{field.name}</code>
          </span>
        </td>
        <td className="px-3 py-2 align-top whitespace-nowrap">
          <code className="font-mono text-surface-500">{typeLabel(spec, field.schema)}</code>
        </td>
        <td className="px-3 py-2 align-top whitespace-nowrap">
          {field.required ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-50 text-[11px] font-medium text-rose-600">
              required
            </span>
          ) : (
            <span className="text-surface-400">{field.nullable ? 'nullable' : 'optional'}</span>
          )}
        </td>
        <td className="px-3 py-2 align-top text-surface-600">
          {field.schema.description}
          {/* With options listed, the default is marked among them instead —
              two statements of the same fact invite disagreement. */}
          {defaultValue !== undefined && !options && (
            <div className="mt-0.5 text-surface-400">
              default <code className="font-mono text-surface-500">{JSON.stringify(defaultValue)}</code>
            </div>
          )}
          {options && (
            <div className="mt-0.5 flex flex-wrap items-baseline gap-1">
              <span className="text-surface-400">one of</span>
              {options.map((option) => {
                const isDefault = JSON.stringify(defaultValue) === JSON.stringify(option);
                return (
                  <span
                    key={option}
                    className={`px-1.5 rounded border text-[11px] ${
                      isDefault
                        ? 'border-primary-200 bg-primary-50 text-primary-700 font-medium'
                        : 'border-surface-200 text-surface-500'
                    }`}
                  >
                    <code className="font-mono">{option}</code>
                    {isDefault && <span className="ml-1 uppercase text-[10px]">default</span>}
                  </span>
                );
              })}
            </div>
          )}
          {pattern && (
            <div className="mt-0.5 text-surface-400">
              must match <code className="font-mono text-surface-500">{pattern}</code>
            </div>
          )}
        </td>
      </tr>
      {open &&
        children.map((child) => (
          <FieldRows key={`${field.name}.${child.name}`} spec={spec} field={child} depth={depth + 1} />
        ))}
    </>
  );
}

/** One object schema as a table, nested objects expanding in place. */
export function SchemaTable({ spec, schema }: { spec: OpenApiSpec; schema: Schema }) {
  const fields = useMemo(() => fieldsOf(spec, schema), [spec, schema]);

  if (!fields.length) return <p className="text-sm text-surface-500">No fields.</p>;

  return (
    <div className="overflow-x-auto rounded-lg border border-surface-200">
      <table className="w-full text-sm">
        <thead className="bg-surface-50 text-surface-500">
          <tr>
            <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Field</th>
            <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Type</th>
            <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Required</th>
            <th className="px-3 py-2 text-left font-medium">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-100">
          {fields.map((field) => (
            <FieldRows key={field.name} spec={spec} field={field} depth={0} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/** One part of the document, and the target its nav entry scrolls to. */
export function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h3 className="text-sm font-semibold text-surface-800 mb-2">{title}</h3>
      {children}
    </section>
  );
}

/** A heading below a section's own — an operation, or an option within one. */
export function SubHeading({ children }: { children: ReactNode }) {
  return <h4 className="mt-6 mb-1 text-sm font-semibold text-surface-700">{children}</h4>;
}

export interface MetaItem {
  label: string;
  value: ReactNode;
}

/** The facts worth stating flatly: base URL, method and path, headers. */
export function MetaGrid({ items }: { items: MetaItem[] }) {
  return (
    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col py-1">
          <dt className="text-[11px] uppercase tracking-wide text-surface-400">{item.label}</dt>
          <dd className="mt-0.5 font-mono text-[0.8125rem] text-surface-700 break-all">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** An aside too small to be a paragraph. */
export function Note({ children }: { children: ReactNode }) {
  return <p className="mt-2 text-[11px] leading-relaxed text-surface-400">{children}</p>;
}
